import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { IconComponent } from '@nagel-assests';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { MatListItem, MatListModule } from '@angular/material/list';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { GroupTourPointComponent } from '../../tour-point/group-tour-point/groupTourPoint.component';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { PickupPlanningTransportOrderEntityType, TourPointConfig } from 'apps/nagel-cal-disposition/src/models/planningPageTypes';
import { DriveInstructionsDrawerService } from '../../../services/drive-instructions-drawer.service';
import { PickupPlanningTransportOrderCard } from '../../pickup-planning-transport-order-card/pickup-planning-transport-order-card.component';
import { FormsModule } from '@angular/forms';
import { EXPANDABLE_TOUR_POINT_TYPES, TransportOrderStatus } from '../../../configuration/consts/pickupPlanning';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ReorderDragAndDropService } from '../../../services/reorder-drag-and-drop.service';
import { PickupPlanningTransportOrdersService } from '../../../services/pickup-planning-trasnport-orders.service';
import { FINISH_TOUR_POINT_TYPE, START_TOUR_POINT_TYPE } from '../../../utils/tourPointsUtils';
import { TourPointType } from 'apps/nagel-cal-disposition/src/models/tourPointTypes';
import { NavigationService } from '../../../services/navigation.service';
import { CalculateRoutesService } from '../../../services/calculate-routes.service';
import { getIsTransportOrderDisabled } from '../../../utils/pickupPlanningUtils';
import { OmsLinkService } from '../../../services/oms-link.service';

@Component({
    selector: 'cal-group-tour-point-list',
    standalone: true,
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatSidenavModule,
        IconComponent,
        MatCheckboxModule,
        MatDivider,
        MatListModule,
        MatListItem,
        DragDropModule,
        MatMenuModule,
        GroupTourPointComponent,
        MatExpansionModule,
        CdkAccordionModule,
        PickupPlanningTransportOrderCard,
        FormsModule,
        MatProgressSpinner
    ],
    templateUrl: './group-tour-point-list.component.html',
    styleUrl: './group-tour-point-list.component.scss'
})
export class GroupTourPointListComponent implements OnInit {
    isVisible = input<boolean>(false);
    tourPoints: TourPointConfig[] = [];
    transportOrderData = signal<PickupPlanningTransportOrderEntityType>({} as PickupPlanningTransportOrderEntityType);
    selectAll: boolean = false;
    selectedTourPointId: number = 0;
    isDragging: boolean = false;
    startIndex: number = 0;
    draggedId: number = 0;
    isDisabled = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    private omsLinkService = inject(OmsLinkService);

    constructor(
        private driveInstructionsDrawerService: DriveInstructionsDrawerService,
        private dragDropService: ReorderDragAndDropService,
        private navigationService: NavigationService,
        private transportOrderCardService: PickupPlanningTransportOrdersService,
        private calculateRoutesService: CalculateRoutesService
    ) {
        this.driveInstructionsDrawerService.refreshTriggerObservable$.subscribe(() => {
            const { transportOrderId } = this.driveInstructionsDrawerService.transportOrderDataSubject.value;
            this.transportOrderCardService.loadingSingleTransportOrderId.set(transportOrderId);
        });

        this.driveInstructionsDrawerService.transportOrderDataSubject.subscribe((value) => {
            this.isDisabled.set(getIsTransportOrderDisabled(value.status));
        });

        //check after release if effect is needed
        effect(() => {
            const updatedOrder = this.transportOrderCardService.singleTransportOrderUpdated();

            if (updatedOrder && updatedOrder.pickupPlanningTransportOrders[0].transportOrderNumber === this.transportOrderData().transportOrderNumber) {
                this.transportOrderData.set({
                    ...updatedOrder.pickupPlanningTransportOrders[0]
                });

                this.transportOrderCardService.singleTransportOrderUpdated.set(null);
            }
        });
    }

    ngOnInit(): void {
        this.driveInstructionsDrawerService.transportOrderDataSubject.subscribe((data: PickupPlanningTransportOrderEntityType ) => {
            if (data.transportOrderId) {
                this.transportOrderData.set(data);
                this.driveInstructionsDrawerService.getTourPointsData(data.transportOrderId);
            }

        });

        this.driveInstructionsDrawerService.tourPointsSubject.subscribe((data: TourPointConfig[]) => {
            this.tourPoints = data;
        });

        this.driveInstructionsDrawerService.isLoadingTourPointsSubject.subscribe((isLoading: boolean) => {
            this.isLoading.set(isLoading);
        });
    }

    getIsLoading(): boolean {
        return this.isLoading();
    }

    getIsSilentUpdateInProgress(): boolean {
        return this.driveInstructionsDrawerService.isSilentUpdateInProgress();
    }

    isExpandableTourPointType(type: number): boolean {
        return EXPANDABLE_TOUR_POINT_TYPES.includes(type);
    }

    handleTourPointClick(tourPoint: TourPointConfig): void {
        this.selectedTourPointId = tourPoint.tourpointId;
        this.driveInstructionsDrawerService.setDrawerDetailsSectionOpened(true);
        this.driveInstructionsDrawerService.setSelectedTourPoint(tourPoint);
        this.driveInstructionsDrawerService.getTourPointDetails(tourPoint);
        this.loadOmsIdsForShipments(tourPoint);
    }

    getIsFromThirdPartySystem = (tourPoint: TourPointConfig): boolean => {
        return this.driveInstructionsDrawerService.isFromThirdPartySystem(tourPoint);
    };

    loadOmsIdsForShipments(tourPoint: TourPointConfig): void {
        const isFromThirdPartySystem = this.getIsFromThirdPartySystem(tourPoint);

        if (!isFromThirdPartySystem) {
            const shipmentIds = tourPoint.legCards.map(leg => leg.shipmentId);

            this.omsLinkService.loadOmsIds(shipmentIds);
        }
    }

    dropTourPoint = (event: CdkDragDrop<TourPointConfig[]>) => {
        if (this.startIndex !== event.currentIndex) {
            this.isDragging = false;

            const {
                transportOrderId
            } = this.driveInstructionsDrawerService.transportOrderDataSubject.value;

            const onSuccess = (tourPoints: TourPointConfig[]) => {
                this.tourPoints = tourPoints;
                this.driveInstructionsDrawerService.retrieveModifiedTransportOrderId();

                this.calculateRoutesService.triggerSilentCalculateRoutesSubject.next(
                    Number(transportOrderId)
                );
            };

            this.dragDropService.dropTourPointReorder<TourPointConfig>(
                event,
                onSuccess,
                this.tourPoints,
                this.driveInstructionsDrawerService.transportOrderDataSubject.value.transportOrderId
            );
        };
    };

    shouldDisableReordering(): boolean {
        return this.isDisabled()
            || this.transportOrderData().status === TransportOrderStatus.Completed
            || this.getIsSilentUpdateInProgress();
    }

    onDrawerClosed(): void {
        this.driveInstructionsDrawerService.setDrawerOpened(false);
    }

    getTourPointClass = (tourPointId: number) => {
        let result = 'flex items-start container grid grid-cols-12 !pt-[8px] !pb-[8px]';

        if (this.selectedTourPointId === tourPointId) {
            result += ' selected-background !pb-[8px] !pt-[8px]';
        }

        return result;
    };

    dragStarted = (index: number, id: number) => {
        this.startIndex = index;
        this.draggedId = id;
        this.isDragging = true;
    };

    shouldDisabledDrag(tourPointType: TourPointType): boolean {
        return [
            FINISH_TOUR_POINT_TYPE,
            START_TOUR_POINT_TYPE
        ].includes(tourPointType);
    }

    navigateToTransportOrderDetails = (transportOrder: PickupPlanningTransportOrderEntityType) => {
        const url = this.navigationService.buildNavigateToDetailsUrl(transportOrder.transportOrderNumber);

        window.open(url, '_blank');
    };

}